'use client'

import { useRef } from 'react'

import Button from 'shared/ui/Button/Button'
import { ButtonSizes } from 'shared/ui/Button/Button.enums'
import TextField from 'shared/ui/form/TextField'
import Modal from 'shared/ui/Modal/Modal'
import { ModalClose } from 'shared/ui/Modal/ModalClose'

import {
  MAX_REASON_NOTE_LENGTH,
  useSaleOrderReturnModal
} from '../../../hooks/useSaleOrderReturnModal'
import { SaleOrder } from '../../../types'

import ProfileSaleOrderDetailsTable from './ProfileSaleOrderDetailsTable'
import ProfileSaleOrderProductsGroupedList from './ProfileSaleOrderProductsGroupedList'
import ProfileSaleOrderSelectableProductCard from './ProfileSaleOrderSelectableProductCard'

import styles from './ProfileSaleOrderReturnModal.module.scss'

interface ProfileSaleOrderReturnModalProps {
  order: SaleOrder
  onSubmitted?: () => void
  triggerButtonSize?: ButtonSizes
}

const ProfileSaleOrderReturnModal = ({
  order,
  onSubmitted,
  triggerButtonSize = 'medium'
}: ProfileSaleOrderReturnModalProps) => {
  const {
    canSubmit,
    changeItemCount,
    getItemMaxReturnQuantity,
    handleClose,
    handleItemToggle,
    handleOpen,
    handleSelectAllItems,
    handleSubmit,
    isReasonNoteTooLong,
    isSubmitting,
    isTmsDelivery,
    open,
    reasonNote,
    saleOrderItems,
    selectedItems,
    selectedItemsCount,
    setReasonNote
  } = useSaleOrderReturnModal({ order, onSubmitted })

  const focusAnchorRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={styles.trigger}>
      <Button
        id='order_return_trigger'
        variant='neutralOutline'
        onClick={handleOpen}
        disabled={isSubmitting || !canSubmit}
        aria-label='Odstąp od umowy'
        size={triggerButtonSize}
      >
        Odstąp od umowy
      </Button>

      <Modal
        id='order-return-modal'
        open={open}
        onClose={handleClose}
        title='Odstąp od umowy'
        size='large'
        closeOnBackdropClick={!isSubmitting}
        closeOnEscape={!isSubmitting}
        hideDefaultCloseButton={true}
        initialFocusAnchor={focusAnchorRef}
      >
        <div className={styles.closeModalWrapper}>
          <ModalClose
            closeButtonLabel='Zamknij okno odstąpienia od umowy'
            ref={focusAnchorRef}
          />
        </div>
        <div className={styles.modalContent}>
          <div className={styles.infoBox}>
            <p>
              <strong>
                Masz prawo do zwrotu zakupionych produktów. Pamiętaj jednak, że
                zwracane produkty należy odesłać na własny koszt, w fabrycznym
                opakowaniu i nienaruszonym stanie.
              </strong>
            </p>
            <p>
              <strong>Produkty NIE PODLEGAJĄCE zwrotowi!</strong>
              <br />
              Produkty o krótkim terminie przydatności do spożycia tj. świeże
              mięso, wędliny, ryby, warzywa, owoce, nabiał, pieczywo oraz
              mrożonki i wszelkie produkty wymagające przechowywania w niskich
              temperaturach.
            </p>
          </div>

          <ProfileSaleOrderDetailsTable
            order={order}
            isTmsDelivery={isTmsDelivery}
            handlePaymentStart={() => {}}
            showPaymentButton={false}
          />
          <div className={styles.selectAllWrapper}>
            <h3 className={styles.selectAllTitle}>Zaznacz produkty do zwrotu:</h3>
            <Button
              variant='neutralOutline'
              onClick={handleSelectAllItems}
              disabled={isSubmitting}
              size={ButtonSizes.small}
            >
              Zaznacz wszystkie
            </Button>
          </div>
          <ProfileSaleOrderProductsGroupedList
            items={saleOrderItems}
            renderItem={item => (
              <ProfileSaleOrderSelectableProductCard
                key={item.id}
                item={item}
                selectedCount={selectedItems[item.id]}
                maxSelectableCount={getItemMaxReturnQuantity(item)}
                isSubmitting={isSubmitting}
                onToggle={handleItemToggle}
                onChangeCount={changeItemCount}
              />
            )}
          />
          <div className={styles.reasonWrapper}>
            <p className={styles.reasonLabel}>Opisz powód zwrotu</p>
            <TextField
              type='textarea'
              rows={4}
              value={reasonNote}
              onChange={event => setReasonNote(event.target.value)}
              placeholder='Opisz powód zwrotu (opcjonalnie)'
              helperText={`${reasonNote.length}/${MAX_REASON_NOTE_LENGTH}`}
              error={isReasonNoteTooLong}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.buttonsRow}>
            <Button
              id='order_return_close_modal'
              fullWidth
              variant='neutralOutline'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Anuluj
            </Button>

            <Button
              id='order_return_submit'
              fullWidth
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={
                !canSubmit ||
                selectedItemsCount === 0 ||
                isReasonNoteTooLong ||
                isSubmitting
              }
            >
              Odstąp od umowy
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ProfileSaleOrderReturnModal
